import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Three equal macro rings (protein / carbs / fat) + kcal ring.
 *
 * Pulls `/api/me/macros/today` on mount. If the user has no calorie
 * profile yet, silently renders nothing so we never guess targets.
 *
 * Props:
 *   refreshKey (any) — bump this from a parent when a new log is posted
 *                      so the rings re-fetch. Optional.
 */
export default function MacroRings({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/me/macros/today");
        if (!cancelled) setData(data);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading || !data) return null;
  const { totals = {}, targets = {} } = data;
  // Nothing to show if the user has no target set yet
  if (!targets.kcal) return null;

  return (
    <div
      className="ff-card p-6 md:p-7 bg-white"
      data-testid="macro-rings-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="ff-label text-[#3B5A3E]">Today&apos;s intake</div>
          <div className="text-lg font-medium mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
            {totals.kcal} / {targets.kcal} kcal
          </div>
        </div>
        <div className="text-xs text-[#5C6B61] text-right leading-snug max-w-[200px]">
          Log recipes or foods and your rings update instantly.
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 sm:gap-5">
        <Ring
          label="kcal"
          current={totals.kcal}
          target={targets.kcal}
          colour="#3B5A3E"
          testid="ring-kcal"
        />
        <Ring
          label="Protein"
          current={totals.protein_g}
          target={targets.protein_g}
          unit="g"
          colour="#A33D26"
          testid="ring-protein"
        />
        <Ring
          label="Carbs"
          current={totals.carbs_g}
          target={targets.carbs_g}
          unit="g"
          colour="#C18928"
          testid="ring-carbs"
        />
        <Ring
          label="Fat"
          current={totals.fat_g}
          target={targets.fat_g}
          unit="g"
          colour="#5A6E88"
          testid="ring-fat"
        />
      </div>
    </div>
  );
}

function Ring({ label, current, target, unit = "", colour, testid }) {
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const R = 34;
  const C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;
  const remaining = Math.max(0, Math.round((target || 0) - (current || 0)));
  return (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <div className="relative" style={{ width: 84, height: 84 }}>
        <svg width="84" height="84" viewBox="0 0 84 84">
          <circle
            cx="42"
            cy="42"
            r={R}
            fill="none"
            stroke="#EFEBE0"
            strokeWidth="8"
          />
          <circle
            cx="42"
            cy="42"
            r={R}
            fill="none"
            stroke={colour}
            strokeWidth="8"
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={C / 4}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 400ms ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-sm font-semibold" style={{ color: colour, fontFamily: "Outfit, sans-serif" }}>
            {pct}%
          </div>
        </div>
      </div>
      <div className="mt-2 text-xs uppercase tracking-wide text-[#8B9690]">{label}</div>
      <div className="text-sm font-medium mt-0.5">
        {Math.round(current || 0)}{unit} / {Math.round(target || 0)}{unit}
      </div>
      <div className="text-[10px] text-[#8B9690] mt-0.5">
        {remaining > 0 ? `${remaining}${unit} to go` : "✓ hit"}
      </div>
    </div>
  );
}
